import QuizOutlinedIcon from '@mui/icons-material/QuizOutlined';
import { Box, Card, CardActionArea, CardContent, Chip, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { brandGradient } from '@/app/theme';
import type { ProductCardData } from '@/types/domain';
import { DemoChip } from './OriginBadge';
import { PriceDisplay } from './PriceDisplay';
import { RatingDisplay } from './RatingDisplay';

/**
 * A product's cover. On a card the title is right beside it, so the image stays
 * silent (alt=""); on the product page itself pass `describe` to give it alt text.
 * `priority` is for the one image at the top of the page (it loads first, not lazily).
 */
export function ProductImage({ src, title, height = 140, describe = false, priority = false }: { src: string | null; title: string; height?: number; describe?: boolean; priority?: boolean }) {
  if (src) {
    return <Box component="img" src={src} alt={describe ? `Cover image for ${title}` : ''} loading={priority ? 'eager' : 'lazy'} decoding="async" fetchPriority={priority ? 'high' : 'auto'} sx={{ width: '100%', height, objectFit: 'cover', display: 'block' }} />;
  }
  // Generated cover so products without artwork still look intentional.
  return (
    <Box
      aria-hidden
      sx={{
        height,
        display: 'grid',
        placeItems: 'center',
        color: 'common.white',
        background: brandGradient,
        borderBottom: (t) => `4px solid ${(t.vars ?? t).palette.secondary.main}`,
        px: 2,
        textAlign: 'center',
      }}
    >
      <Stack sx={{ alignItems: 'center' }} spacing={0.5}>
        <QuizOutlinedIcon sx={{ color: 'secondary.main' }} />
        <Typography variant="subtitle2" sx={{ opacity: 0.9, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {title}
        </Typography>
      </Stack>
    </Box>
  );
}

export function ProductCard({ product }: { product: ProductCardData }) {
  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardActionArea component={RouterLink} to={`/store/${product.slug}`} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
        <ProductImage src={product.image_url} title={product.title} />
        <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.75, '& > *': { m: '0 !important' } }}>
            {product.product_type === 'bundle' && <Chip size="small" color="primary" label={`Bundle · ${product.bundle_size ?? 0} products`} />}
            {product.category && <Chip size="small" label={product.category.name} />}
            {product.is_demo && <DemoChip />}
            {product.sale_price_cents != null && <Chip size="small" color="secondary" label="Sale" />}
          </Stack>
          <Typography variant="h6" component="h3" sx={{ lineHeight: 1.3 }}>
            {product.title}
          </Typography>
          {product.subtitle && (
            <Typography variant="body2" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              {product.subtitle}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            by {product.provider.display_name}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <RatingDisplay value={product.rating_avg} count={product.rating_count} />
          <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {product.question_count.toLocaleString()} questions{product.version ? ` · v${product.version}` : ''}
            </Typography>
            <PriceDisplay
              priceCents={product.price_cents}
              salePriceCents={product.sale_price_cents}
              currency={product.currency}
              size="small"
              saleMode={product.sale_mode}
              subscriptionPriceCents={product.subscription_price_cents}
            />
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}
